import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí iría la lógica de envío del formulario
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <motion.section 
      id="conecta-con-nosotros" 
      className="py-20 bg-deep-50 text-white relative overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {/* Marine background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-jade-400 rounded-full filter blur-3xl animate-bubble-float"></div>
        <div className="absolute bottom-32 right-16 w-48 h-48 bg-ocean-500 rounded-full filter blur-3xl animate-gentle-sway"></div>
        <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-jade-300 rounded-full filter blur-2xl animate-tidal-flow"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={itemVariants}
        >
          <motion.h2 
            className="text-4xl font-bold mb-4 bg-gradient-to-r from-jade-500 to-ocean-500 bg-clip-text text-transparent"
            variants={itemVariants}
          >
            Conecta con Nosotros
          </motion.h2>
          <motion.p 
            className="text-xl text-pearl-700 max-w-3xl mx-auto"
            variants={itemVariants}
          >
            Estamos aquí para hacer de tu experiencia gastronómica algo extraordinario. 
            Contáctanos para reservas o consultas especiales.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div 
            className="space-y-8"
            variants={containerVariants}
          >
            <motion.h3 
              className="text-2xl font-bold text-ocean-500 mb-6"
              variants={itemVariants}
            >
              Información de Contacto
            </motion.h3>

            <motion.div 
              className="space-y-6"
              variants={containerVariants}
            >
              {[
                { icon: MapPin, title: "Ubicación", info: "Costa Atlántica, Argentina", subinfo: "Frente al mar con vista panorámica" },
                { icon: Phone, title: "Teléfono", info: "+54 223 456-7890", subinfo: "Disponible para reservas" },
                { icon: Mail, title: "Email", info: "info@mardejade.com", subinfo: "Respuesta en 24 horas" },
                { icon: Clock, title: "Horarios", info: "Lun - Dom: 8:00 - 22:00", subinfo: "Abierto todos los días" }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex items-start space-x-4 p-6 bg-slate-800/50 rounded-xl backdrop-blur-sm border border-slate-700 hover:border-jade-500 transition-colors group"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    className="bg-gradient-to-r from-jade-500 to-ocean-500 p-3 rounded-lg group-hover:scale-110 transition-transform"
                    whileHover={{ rotate: 5 }}
                  >
                    <item.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-jade-300 mb-1">{item.title}</h4>
                    <p className="text-white font-medium">{item.info}</p>
                    <p className="text-gray-400 text-sm">{item.subinfo}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={itemVariants}>
            <motion.h3 
              className="text-2xl font-bold text-jade-300 mb-6"
              variants={itemVariants}
            >
              Envíanos un Mensaje
            </motion.h3>

            <motion.form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre Completo
                </label>
                <motion.input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800/70 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-jade-500 focus:border-transparent text-white placeholder-gray-400 transition-all"
                  placeholder="Tu nombre completo"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Correo Electrónico
                </label>
                <motion.input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800/70 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-jade-500 focus:border-transparent text-white placeholder-gray-400 transition-all"
                  placeholder="tu.email@ejemplo.com"
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Mensaje
                </label>
                <motion.textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-800/70 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-jade-500 focus:border-transparent text-white placeholder-gray-400 resize-none transition-all"
                  placeholder="Cuéntanos sobre tu consulta o reserva especial..."
                  whileFocus={{ scale: 1.02 }}
                />
              </motion.div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-jade-600 to-ocean-600 text-white font-bold py-4 px-6 rounded-lg hover:from-jade-700 hover:to-ocean-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send className="w-5 h-5" />
                <span>Enviar Mensaje</span>
              </motion.button>
            </motion.form>
          </motion.div>
        </div>

        {/* WhatsApp CTA */}
        <motion.div 
          className="mt-16 text-center"
          variants={itemVariants}
        >
          <motion.div
            className="inline-block p-6 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl shadow-2xl"
            whileHover={{ scale: 1.05, rotate: 1 }}
            whileTap={{ scale: 0.95 }}
          >
            <h4 className="text-xl font-bold text-white mb-2">¿Necesitas una respuesta rápida?</h4>
            <p className="text-green-100 mb-4">Contáctanos directamente por WhatsApp</p>
            <motion.a
              href="https://wa.me/5492234567890"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-deep-200 text-green-600 font-bold px-6 py-3 rounded-full hover:bg-deep-100 transition-colors border border-green-600/30"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Escribir por WhatsApp
            </motion.a>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating marine elements */}
      <motion.div
        className="absolute top-1/4 right-10 opacity-20"
        animate={{
          y: [-15, 15, -15],
          rotate: [0, 10, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-16 h-16 bg-gradient-to-br from-jade-400 to-ocean-400 rounded-full filter blur-xl" />
      </motion.div>

      <motion.div
        className="absolute bottom-1/4 left-8 opacity-15"
        animate={{
          y: [20, -20, 20],
          x: [-10, 10, -10],
          scale: [0.8, 1.3, 0.8]
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      >
        <div className="w-24 h-24 bg-gradient-to-br from-ocean-300 to-jade-300 rounded-full filter blur-2xl" />
      </motion.div>
    </motion.section>
  );
};

export default Contact;
